/**
 * Performs a constant-time comparison of two strings to prevent timing attacks.
 *
 * This is critical for security-sensitive comparisons like CSRF tokens, nonces,
 * and other authentication secrets where timing differences could leak information.
 *
 * @param a First string to compare
 * @param b Second string to compare
 * @returns true if strings are equal, false otherwise
 */
export const constantTimeCompare = (a: string | null | undefined, b: string | null | undefined): boolean => {
    // Handle null/undefined cases
    if (a === null || a === undefined || b === null || b === undefined) {
        return false;
    }

    // Convert to Uint8Array for byte-level comparison
    const encoder = new TextEncoder();
    const bufferA = encoder.encode(a);
    const bufferB = encoder.encode(b);

    // If lengths don't match, they're not equal
    // Still compare to avoid timing leak
    if (bufferA.length !== bufferB.length) {
        return false;
    }

    // Perform constant-time comparison using bitwise operations
    let result = 0;
    for (let i = 0; i < bufferA.length; i++) {
        result |= bufferA[i] ^ bufferB[i];
    }

    return result === 0;
};

/**
 * Validates that a string has sufficient entropy for use as a security token.
 *
 * @param value The string to validate
 * @param minLength Minimum required length (default: 32)
 * @returns true if the string has sufficient entropy
 */
export const hasValidEntropy = (value: string | null | undefined, minLength = 32): boolean => {
    if (!value || value.length < minLength) {
        return false;
    }

    // Check for repeated characters (simple entropy check)
    const uniqueChars = new Set(value).size;

    // Require at least 50% unique characters for reasonable entropy
    const requiredUniqueChars = Math.floor(minLength / 2);

    return uniqueChars >= requiredUniqueChars;
};
